import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormattedMessage, useIntl } from 'react-intl';

import Modal from '@/shared/ui/Modal';
import Input from '@/shared/ui/Input';
import Button from '@/shared/ui/Button';
import { useToggleModalState } from '@/shared/hooks/useToggleModalState';

import { discountRequestSchema, type DiscountRequestFormValues } from '~/marketing/model/discountRequestSchema';
import { MARKETING_MODALS } from '~/marketing/constants/modals';

const GOOGLE_FORM_ENDPOINT = 'https://docs.google.com/forms/d/1UXLWYBAz7OjfHUUdiT3GNETon3j2lmlS2WYNYEM6bLk/formResponse';

const FORM_ENTRIES = {
  currentHashrate: 'entry.1963222293',
  name: 'entry.2075257781',
  email: 'entry.1792295635',
  contact: 'entry.1684013546',
} as const;

const buildFormUrl = (values: DiscountRequestFormValues): string => {
  const params = new URLSearchParams();
  params.set('usp', 'pp_url');
  params.set(FORM_ENTRIES.currentHashrate, values.currentHashrate);
  params.set(FORM_ENTRIES.name, values.name);
  params.set(FORM_ENTRIES.email, values.email);
  if (values.contact) {
    params.set(FORM_ENTRIES.contact, values.contact);
  }
  params.set('submit', 'Submit');

  return `${GOOGLE_FORM_ENDPOINT}?${params.toString()}`;
};

export const DiscountRequestModal = () => {
  const intl = useIntl();
  const { isShow, closeModal } = useToggleModalState({
    name: MARKETING_MODALS.DISCOUNT_REQUEST,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DiscountRequestFormValues>({
    resolver: zodResolver(discountRequestSchema),
  });

  const formatFieldError = useCallback((fieldError?: { message?: string } | null) => {
    if (!fieldError?.message) {
      return null;
    }

    return intl.formatMessage({ id: fieldError.message });
  }, [intl]);

  const onSubmit = async (values: DiscountRequestFormValues) => {
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const url = buildFormUrl(values);
      await fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      reset();
      setIsSuccess(true);
    } catch (_error) {
      setSubmitError('marketing.discount.form.submit.error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isShow) {
      setSubmitError(null);
      setIsSuccess(false);
      reset();
    }
  }, [isShow, reset]);

  const handleClose = () => {
    setIsSuccess(false);
    setSubmitError(null);
    reset();
    closeModal();
  };

  return (
    <Modal show={isShow} onDismiss={handleClose} size="sm">
      <div className="mb-4">
        <h3 className="text-2xl text-white mb-2">
          <FormattedMessage id="marketing.discount.modal.title" />
        </h3>
      </div>

      {isSuccess ? (
        <div className="flex flex-col items-start">
          <p className="text-sm text-gray-200 mb-6 leading-5">
            <FormattedMessage id="marketing.discount.success.subtitle" />
          </p>
          <div className="flex justify-end w-full">
            <Button
              type="button"
              className="min-w-[120px]"
              colorSchema="primary"
              onClick={handleClose}
            >
              <FormattedMessage id="marketing.discount.success.cta" />
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input
            className="mb-4"
            type="number"
            step="any"
            inputMode="decimal"
            full
            label={intl.formatMessage({ id: 'marketing.discount.form.hashrate.label' })}
            hint={intl.formatMessage({ id: 'marketing.discount.form.hashrate.hint' })}
            error={formatFieldError(errors.currentHashrate)}
            {...register('currentHashrate')}
          />

          <Input
            className="mb-4"
            type="text"
            full
            label={intl.formatMessage({ id: 'marketing.discount.form.name.label' })}
            error={formatFieldError(errors.name)}
            {...register('name')}
          />

          <Input
            className="mb-4"
            type="email"
            full
            label={intl.formatMessage({ id: 'marketing.discount.form.email.label' })}
            error={formatFieldError(errors.email)}
            {...register('email')}
          />

          <Input
            type="text"
            full
            label={intl.formatMessage({ id: 'marketing.discount.form.telegram.label' })}
            labelPostfix={intl.formatMessage({ id: 'optional' })}
            error={formatFieldError(errors.contact)}
            {...register('contact')}
          />

          {submitError ? (
            <p className="text-sm text-red-400 mt-4">
              {intl.formatMessage({ id: submitError })}
            </p>
          ) : null}

          <div className="flex justify-end mt-6">
            <Button onClick={handleClose} type="button" className="min-w-[88px]">
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              isLoading={isSubmitting}
              type="submit"
              className="ml-5 min-w-[88px]"
              colorSchema="primary"
            >
              <FormattedMessage id="marketing.discount.form.submit" />
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default DiscountRequestModal;

